import { createClient, type SupabaseClient } from '@supabase/supabase-js';

/**
 * Browser-side Supabase client singleton.
 *
 * Reads `VITE_SUPABASE_URL` and `VITE_SUPABASE_ANON_KEY` from the Vite env
 * (set in `portal/.env.local` for dev, project-level env vars for production).
 *
 * The `anon` (publishable) key is safe to bundle — it's gated by Row-Level
 * Security policies. The `service_role` key must NEVER appear in the browser
 * bundle and is intentionally not read here.
 *
 * Usage:
 *
 *   import { supabase } from '@/shared/lib/supabase';
 *   const { data, error } = await supabase
 *     .from('invoice')
 *     .select('*')
 *     .order('issue_date', { ascending: false });
 *
 * The client persists session in localStorage by default. Sign-out flows
 * must call `supabase.auth.signOut()` to clear it.
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  // Don't throw at import time — sign-in is the only flow that needs this in
  // dev today. Fail loudly on first call instead, so the rest of the SPA can
  // still render (mock-data routes still work).
  console.warn(
    '[Supabase] VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY missing in portal/.env.local. ' +
      'Auth and database calls will fail until these are set.'
  );
}

let _client: SupabaseClient | null = null;

/**
 * Lazy-initialised singleton. Throws on first call if env vars are missing.
 * Use `supabase` (the proxy) in calling code; this function is exported only
 * for tests that want to swap out the underlying client.
 */
export function getSupabaseClient(): SupabaseClient {
  if (_client) return _client;
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    throw new Error(
      'Supabase env vars missing. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in portal/.env.local.'
    );
  }
  _client = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
      // Persist session in localStorage. Disable to make sign-in stateless.
      persistSession: true,
      // Auto-refresh the access token before it expires (default 1h).
      autoRefreshToken: true,
      // Disabled: SignInPage takes explicit control of code exchange under
      // PKCE. With React StrictMode + detectSessionInUrl=true the SDK races
      // with the React effect and either: (a) consumes the code before the
      // effect mounts, or (b) is called twice and fails the second exchange
      // (PKCE codes are single-use). Explicit handling is the documented
      // pattern for PKCE password-reset flows.
      detectSessionInUrl: false,
      // Refresh-token rotation matches the project config.toml setting.
      flowType: 'pkce',
    },
    global: {
      headers: {
        // Identifies this client in Supabase's request logs.
        'x-application-name': 'afrilink-portal',
      },
    },
  });
  return _client;
}

/**
 * Default export — proxy to the singleton.
 * `supabase.from(...)`, `supabase.auth.signInWithPassword(...)`, etc.
 */
export const supabase: SupabaseClient = new Proxy({} as SupabaseClient, {
  get(_target, prop) {
    return Reflect.get(getSupabaseClient(), prop);
  },
});
